import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePlatform } from '@/providers/PlatformProvider';
import { ComingSoon } from '@/components/system/ComingSoon';
import { MaintenancePage } from '@/components/system/MaintenancePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import type { FeatureKey, MaintenanceScope } from '@/types/platform';

/**
 * Gates a route behind a feature flag — BUILD-PLAN §23/§54/§65.
 *
 * This is the frontend half of the Coming Soon guarantee. It is a UX control,
 * not a security control: the API enforces the same flag independently via
 * the `feature` middleware, so bypassing this in the browser gains nothing.
 *
 * State mapping:
 *   enabled     → render the route
 *   coming_soon → branded ComingSoon page, using admin-authored copy if set
 *   disabled    → 404, so a switched-off feature does not disclose it exists
 *   unknown key → 404, failing closed exactly as the backend does
 */

interface FeatureRouteProps {
  feature: FeatureKey;
  /** Maintenance scope to check alongside the flag, where one applies. */
  maintenanceScope?: MaintenanceScope;
  children: ReactNode;
}

export function FeatureRoute({ feature, maintenanceScope, children }: FeatureRouteProps) {
  const { feature: getFeature, inMaintenance, isPreLaunch } = usePlatform();

  /*
   | Before launch, nothing is reachable — §24, §67.
   |
   | Checked first, and here rather than only in the layout, because the auth
   | screens (`/login`, `/register`, …) sit OUTSIDE the public shell so they
   | can render without shop navigation. Without this a visitor who typed one
   | of those URLs would walk straight past the holding page into a working
   | sign-in form for a shop that has not opened.
   */
  if (isPreLaunch) {
    return <Navigate to="/" replace />;
  }

  // Maintenance is checked first: an enabled feature that is temporarily
  // offline should say so, rather than claim it is still being built.
  if (maintenanceScope && inMaintenance(maintenanceScope)) {
    return <MaintenancePage fullScreen={false} />;
  }

  const resolved = getFeature(feature);

  if (!resolved || resolved.state === 'disabled') {
    return <NotFoundPage />;
  }

  if (resolved.state === 'coming_soon') {
    return (
      <ComingSoon
        title={resolved.title}
        description={resolved.message}
        featureName={resolved.label}
      />
    );
  }

  return <>{children}</>;
}
